//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";

import moment from "moment-timezone";
import { ZonedMoment, getRenderTimeZone } from "@util/time-zone.util";
import { ICalViewState } from "@reducers/chrome-extension.reducer";
import { calIdPersonMapFromPersons, calIdOrgPersonMapFromOrgPersons } from "@util/calendar-id.util";
import { getAbsoluteDate } from "@clockwise/client-commons/src/util/format-time";
import { validateOrgWithTeamHealth, validateOrgWithTeamHealthMaxMinutes } from "@util/helpers.util";
import { convertDateStringToMoment } from "@util/date.util";

//////////////////
// TYPES
//////////////////
export interface IScoredPerson {
  calendarFocusTimeScore: ISchema.ICalendarFocusTimeScore;
  person: ISchema.IPerson | null;
  orgPerson: ISchema.IOrgPerson | null;
  maxMinutes?: number;
}

export enum EHealthTimePeriod {
  TwoWeeksAgo,
  LastWeek,
  ThisWeek,
  NextWeek,
  TwoWeeksAhead,
  CalendarView,
  LastMonth,
  Last3Months,
}

interface IFetchDates {
  start: moment.Moment;
  end: moment.Moment;
  invalidRange: boolean;
}

export interface IHealthFetchVars {
  orgRelayId: string;
  timeZone: string;
  rangeMin: ISchema.IDayTimeSlotInput;
  rangeMax: ISchema.IDayTimeSlotInput;
  calendarId?: string;
}

export interface IHealthFetchInfo extends IFetchDates {
  fetchVariables: IHealthFetchVars;
  dateLabel: string;
}

export const GOAL_MINUTES = 800;

//////////////////
// HELPERS
//////////////////
// generate 'YYYY-MM-DD' from calViewState
function generateDateString(calViewState: ICalViewState) {
  const { year, month, selectedDay } = calViewState;
  return `${year}-${month}-${selectedDay}`;
}

//////////////////
// VARIABLES
//////////////////
// simple helper to construct dayTimeSlotInputs for a given start and end date
function generateRangeMinMax(start: moment.Moment, end: moment.Moment) {
  const rangeMin: ISchema.IDayTimeSlotInput = {
    date: start.format("YYYY-MM-DD"),
    timeSlot: "T_00_00",
  } as any; // FIXME: TEMP FIX FOR GQL2TS ISSUES
  const rangeMax: ISchema.IDayTimeSlotInput = {
    date: end.format("YYYY-MM-DD"),
    timeSlot: "T_23_30",
  } as any; // FIXME: TEMP FIX FOR GQL2TS ISSUES
  return { rangeMin, rangeMax };
}

// this will take the calViewState and get a start and end date
function getFetchDatesForCalState(calViewState: ICalViewState | null): IFetchDates {
  const today = moment().tz(getRenderTimeZone());
  let selectedDate = today;

  // important! set selectedDate from the calViewState, if we have one
  if (!!calViewState) {
    const calDateString = generateDateString(calViewState);
    selectedDate = moment.tz(calDateString, "YYYY-MM-DD", getRenderTimeZone());
  }

  // we're only sending back a given week, for now
  let start = selectedDate.clone().startOf("week");
  let end = selectedDate.clone().endOf("week");
  let invalidRange = false;

  // backend only supports 3 month ranges, for now
  if (Math.abs(today.diff(start, "months")) >= 3 || Math.abs(today.diff(end, "months")) >= 3) {
    // outside of 3 month range allowed by backend
    start = today.clone().startOf("week");
    end = today.clone().startOf("week");
    invalidRange = true;
  }

  return { start, end, invalidRange };
}

// this will generate a start and end date for a given period selection
function getFetchDatesForPeriod(period: EHealthTimePeriod): IFetchDates {
  const today = moment().tz(getRenderTimeZone());
  let start: moment.Moment;
  let end: moment.Moment;

  switch (period) {
    case EHealthTimePeriod.TwoWeeksAgo:
      start = today.clone().subtract(2, "weeks").startOf("week");
      end = today.clone().subtract(2, "weeks").endOf("week");
      break;
    case EHealthTimePeriod.LastWeek:
      start = today.clone().subtract(1, "weeks").startOf("week");
      end = today.clone().subtract(1, "weeks").endOf("week");
      break;
    case EHealthTimePeriod.NextWeek:
      start = today.clone().add(1, "weeks").startOf("week");
      end = today.clone().add(1, "weeks").endOf("week");
      break;
    case EHealthTimePeriod.TwoWeeksAhead:
      start = today.clone().add(2, "weeks").startOf("week");
      end = today.clone().add(2, "weeks").endOf("week");
      break;
    case EHealthTimePeriod.Last3Months:
      start = today.clone().subtract(12, "weeks").startOf("week");
      end = today.clone().subtract(1, "weeks").endOf("week");
      break;
    case EHealthTimePeriod.LastMonth:
      start = today.clone().subtract(4, "weeks").startOf("week");
      end = today.clone().subtract(1, "weeks").endOf("week");
      break;
    case EHealthTimePeriod.ThisWeek:
    default:
      start = today.clone().startOf("week");
      end = today.clone().endOf("week");
      break;
  }

  return { start, end, invalidRange: false };
}

function getLabel({ start, end, invalidRange }: IFetchDates) {
  if (invalidRange) {
    return "Outside Range";
  } else if (new ZonedMoment().startOf("week").isSame(start)) {
    return "This Week";
  } else if (new ZonedMoment().startOf("week").subtract(1, "weeks").isSame(start)) {
    return "Last Week";
  } else if (new ZonedMoment().startOf("week").add(1, "weeks").isSame(start)) {
    return "Next Week";
  } else {
    return getAbsoluteDate(new ZonedMoment(start), new ZonedMoment(end));
  }
}

export function getFetchInfo(
  orgRelayId: string,
  calViewState: ICalViewState | null,
  period = EHealthTimePeriod.CalendarView,
): IHealthFetchInfo {
  // get the dates from the calViewState if that's the period we're selected for
  const fetchDates =
    period === EHealthTimePeriod.CalendarView
      ? getFetchDatesForCalState(calViewState)
      : getFetchDatesForPeriod(period);

  // generate min/max timeslots and create a nice set of fetch variables
  const ranges = generateRangeMinMax(fetchDates.start, fetchDates.end);
  const fetchVariables = {
    orgRelayId,
    timeZone: getRenderTimeZone(),
    ...ranges,
  };
  const dateLabel = getLabel(fetchDates);
  return { fetchVariables, dateLabel, ...fetchDates };
}

////////////////////
// CAL VIEW STATE
////////////////////
export function getCalViewStateFromDateString(viewWeek: string): ICalViewState {
  const m = convertDateStringToMoment(viewWeek);
  return {
    activeView: "week",
    year: m.year(),
    month: m.month() + 1, // jan is 0 indexed
    selectedDay: m.date(),
  };
}

export function getHealthPeriodFromCalViewState(calViewState: ICalViewState | null) {
  if (!calViewState) {
    return null;
  }
  const dateString = generateDateString(calViewState);
  const selectedWeek = moment.tz(dateString, "YYYY-MM-DD", getRenderTimeZone()).startOf("week");
  const today = moment().tz(getRenderTimeZone());

  switch (calViewState.activeView) {
    case "agenda":
    case "day":
    case "custom":
    case "week":
      if (today.clone().subtract(2, "weeks").startOf("week").isSame(selectedWeek)) {
        return EHealthTimePeriod.TwoWeeksAgo;
      } else if (today.clone().subtract(1, "weeks").startOf("week").isSame(selectedWeek)) {
        return EHealthTimePeriod.LastWeek;
      } else if (today.clone().startOf("week").isSame(selectedWeek)) {
        return EHealthTimePeriod.ThisWeek;
      } else if (today.clone().add(1, "weeks").startOf("week").isSame(selectedWeek)) {
        return EHealthTimePeriod.NextWeek;
      } else if (today.clone().add(2, "weeks").startOf("week").isSame(selectedWeek)) {
        return EHealthTimePeriod.TwoWeeksAhead;
      } else {
        return null;
      }
    case "year":
    case "month":
      return null;
    default:
      return null;
  }
}

//////////////////
// DATA HANDLING
//////////////////
// creates an object with the person that maps to the score
function collateScoreAndPerson(
  calendarFocusTimeScore: ISchema.ICalendarFocusTimeScore,
  personMap: { [calId: string]: ISchema.IPerson },
  orgPersonMap: { [calId: string]: ISchema.IOrgPerson },
  maxMinutes?: number,
): IScoredPerson {
  const person = personMap[calendarFocusTimeScore.calendarId] || null;
  const orgPerson = orgPersonMap[calendarFocusTimeScore.calendarId] || null;
  return { person, orgPerson, calendarFocusTimeScore, maxMinutes };
}

// sorts alphabetically by firstname (or calendarId, if profile is missing)
function sortScoredPersons(a: IScoredPerson, b: IScoredPerson) {
  const aName =
    a.person && a.person.profile ? a.person.profile.givenName : a.calendarFocusTimeScore.calendarId;
  const bName =
    b.person && b.person.profile ? b.person.profile.givenName : b.calendarFocusTimeScore.calendarId;
  if (aName < bName) {
    return -1;
  } else if (aName > bName) {
    return 1;
  } else {
    return 0;
  }
}

// collates userProfiles and scores, and then sorts alphabetically
function sortAndCollateScoresAndPersons(
  teamHealthScore: ISchema.ITeamHealthScore,
  personMap: { [calId: string]: ISchema.IPerson },
  orgPersonMap: { [calId: string]: ISchema.IOrgPerson },
  maxMinutes?: number,
): IScoredPerson[] {
  const { myFocusTimeScore, calendarFocusTimeScores } = teamHealthScore;

  // first we pull out the "myFocusTime"
  const myPair = collateScoreAndPerson(myFocusTimeScore, personMap, orgPersonMap, maxMinutes);

  // then sort the remaining team scores
  const teamPairs = calendarFocusTimeScores
    .map((calendarFocusTimeScore) => {
      return collateScoreAndPerson(calendarFocusTimeScore, personMap, orgPersonMap, maxMinutes);
    })
    .sort(sortScoredPersons);

  // put them in one array for convenience, with personal first
  return [myPair, ...teamPairs];
}

// this peels the team-health-scores off the org
// and the persons and then collates them based on
// calendarId into a nice set of scores and profiles
export function getCollatedPersonsAndScoresOffOrg(
  org: ISchema.IOrg,
  fastCrawlStillProcessing?: boolean,
  userHasNoTeams?: boolean,
): IScoredPerson[] {
  const isValidFragment = validateOrgWithTeamHealth(org, userHasNoTeams);
  if (!isValidFragment) {
    return [];
  }
  const teamHealthScoreErrorable = userHasNoTeams
    ? org.teamHealthScoreJustForUserErrorable
    : org.teamHealthScoreForTeamErrorable;

  if (teamHealthScoreErrorable.__typename === "GraphEntityError") {
    return [];
  }

  return getCollatedPersonsAndScoresOffOrgWithTeamHealthScore(
    org,
    teamHealthScoreErrorable,
    fastCrawlStillProcessing,
    userHasNoTeams,
  );
}

export function getCollatedPersonsAndScoresOffOrgWithTeamHealthScore(
  org: ISchema.IOrg,
  teamHealthScore: ISchema.ITeamHealthScore,
  fastCrawlStillProcessing?: boolean,
  userHasNoTeams?: boolean,
): IScoredPerson[] {
  const { myFocusTimeScore, calendarFocusTimeScores } = teamHealthScore;

  let persons: ISchema.IPerson[] = [];
  if (teamHealthScore.persons.__typename === "PersonList") {
    persons = teamHealthScore.persons.list;
  }

  let orgPersons: ISchema.IOrgPerson[] = [];
  if (teamHealthScore.orgPersons && teamHealthScore.orgPersons.__typename === "OrgPersonList") {
    orgPersons = teamHealthScore.orgPersons.list;
  }

  // get profiles for calendarIds
  const teamCalIds = calendarFocusTimeScores.map((cfts) => cfts.calendarId);
  const allCalIds = [myFocusTimeScore.calendarId, ...teamCalIds];
  const personMap = calIdPersonMapFromPersons(persons, allCalIds);
  const orgPersonMap = calIdOrgPersonMapFromOrgPersons(orgPersons, allCalIds);

  // get largest set of totalMinutes
  const maxMinutes = !!fastCrawlStillProcessing
    ? undefined
    : maybeGetMaxMinutesForOrg(org, userHasNoTeams);

  // pairs all the scores/profiles and sorts properly
  return sortAndCollateScoresAndPersons(teamHealthScore, personMap, orgPersonMap, maxMinutes);
}

// gets the largest totalMinutes value returned on teamHealth
export function maybeGetMaxMinutesForOrg(
  org: ISchema.IOrg,
  userHasNoTeams?: boolean,
): number | undefined {
  const isValidFragment = validateOrgWithTeamHealthMaxMinutes(org, userHasNoTeams);
  if (!isValidFragment) {
    return undefined;
  }
  const teamHealthScoreErrorable = userHasNoTeams
    ? org.teamHealthScoreJustForUserErrorable
    : org.teamHealthScoreForTeamErrorable;

  if (teamHealthScoreErrorable.__typename === "GraphEntityError") {
    return undefined;
  }

  const { myFocusTimeScore, calendarFocusTimeScores } = teamHealthScoreErrorable;

  const allTotalMinutes = [
    myFocusTimeScore.totalMinutes,
    ...calendarFocusTimeScores.map((cfts) => cfts.totalMinutes),
  ];
  const maxMinutes = Math.max.apply(Math, allTotalMinutes);
  return Number.isInteger(maxMinutes) ? maxMinutes : undefined;
}

// Determine if CalendarFocusTimeScore is "synced" or not. Synced when status is Synced or Stale.
// VeryStale is considered not synced.
export function isFocusTimeCrawledStatusSynced(focusTimeStatus: ISchema.CrawledStatus): boolean {
  return (
    focusTimeStatus === ISchema.CrawledStatus.Synced ||
    focusTimeStatus === ISchema.CrawledStatus.Stale
  );
}
